import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { AuthContextType, User, LoginCredentials, AuthResponse } from '../types/auth';
import { authAPI } from '../services/auth';
import { msalInstance, flowApiTokenRequest, isMicrosoftSignInConfigured } from '../services/msalConfig';
import { AUTH_EXPIRED_EVENT, isTokenExpired } from '../utils/authToken';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
}

interface AuthProviderProps {
  children: ReactNode;
}

const completeMicrosoftRedirect = async () => {
  await msalInstance.initialize();
  try {
    return await msalInstance.handleRedirectPromise();
  } catch (error) {
    // handleRedirectPromise clears temporary request state even when an older
    // interaction could not complete (e.g. a popup abandoned before a reload).
    console.warn('[FIX] Microsoft sign-in response could not be completed:', error);
    return null;
  }
};

const getMicrosoftAccount = (): AccountInfo | null => {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  if (account) {
    msalInstance.setActiveAccount(account);
  }
  return account;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const msalLoginInFlight = useRef(false);

  const storeMicrosoftAccessToken = useCallback(async (accessToken: string, isActive: () => boolean = () => true) => {
    const response: AuthResponse = await authAPI.loginWithMicrosoft(accessToken);
    if (!isActive()) return;

    const decoded = jwtDecode<JwtPayload>(response.token);
    setToken(response.token);
    setUser({
      id: decoded.sub || '',
      email: response.email,
      name: decoded.name
    });
    localStorage.setItem('token', response.token);
  }, []);

  const logout = useCallback((): void => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  }, []);

  useEffect(() => {
    let active = true;
    const initializeAuth = async () => {
      let hasFlowToken = false;
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decoded = jwtDecode<JwtPayload>(storedToken);

          if (!isTokenExpired(storedToken)) {
            hasFlowToken = true;
            setToken(storedToken);
            setUser({
              id: decoded.sub || '',
              email: decoded.email || '',
              name: decoded.name
            });
          } else {
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Invalid token:', error);
          localStorage.removeItem('token');
        }
      }

      try {
        if (!isMicrosoftSignInConfigured) return;

        const redirectResult = await completeMicrosoftRedirect();
        if (!active) return;
        if (redirectResult) {
          if (!redirectResult.accessToken) {
            throw new Error('Microsoft sign-in did not return a Flow API access token.');
          }
          await storeMicrosoftAccessToken(redirectResult.accessToken, () => active);
        } else if (!hasFlowToken) {
          const account = getMicrosoftAccount();
          if (account) {
            let accessToken: string;
            try {
              const result = await msalInstance.acquireTokenSilent({ ...flowApiTokenRequest, account });
              accessToken = result.accessToken;
            } catch (error) {
              // No interactive sign-in on startup; the login button can start one.
              console.info('[FIX] Microsoft session needs a new sign-in:', error);
              return;
            }
            await storeMicrosoftAccessToken(accessToken, () => active);
          }
        }
      } catch (error) {
        console.error('Microsoft sign-in restoration failed:', error);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    void initializeAuth();
    return () => { active = false; };
  }, [storeMicrosoftAccessToken]);

  // React instantly when any API call discovers an expired token,
  // and re-validate when the user comes back to the app.
  useEffect(() => {
    const handleExpiry = () => logout();
    const revalidateOnActivity = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && isTokenExpired(storedToken)) {
        logout();
      }
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpiry);
    window.addEventListener('focus', revalidateOnActivity);
    document.addEventListener('visibilitychange', revalidateOnActivity);

    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpiry);
      window.removeEventListener('focus', revalidateOnActivity);
      document.removeEventListener('visibilitychange', revalidateOnActivity);
    };
  }, [logout]);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setIsLoading(true);
    try {
      const response: AuthResponse = await authAPI.login(credentials);
      const decoded = jwtDecode<JwtPayload>(response.token);
      
      setToken(response.token);
      setUser({
        id: decoded.sub || '',
        email: response.email,
        name: decoded.name
      });
      
      localStorage.setItem('token', response.token);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithMicrosoft = async (): Promise<void> => {
    if (!isMicrosoftSignInConfigured) {
      throw new Error('Microsoft sign-in is not configured.');
    }

    if (msalLoginInFlight.current) {
      console.warn('[FIX] Microsoft sign-in already in progress; ignoring duplicate attempt.');
      return;
    }

    msalLoginInFlight.current = true;
    setIsLoading(true);
    try {
      await completeMicrosoftRedirect();
      const cachedAccount = getMicrosoftAccount();
      if (cachedAccount) {
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...flowApiTokenRequest,
            account: cachedAccount,
          });
          await storeMicrosoftAccessToken(tokenResponse.accessToken);
          return;
        } catch (error) {
          if (!(error instanceof InteractionRequiredAuthError)) {
            throw error;
          }
          console.info('[FIX] Cached Microsoft account requires an interactive sign-in.');
        }
      }

      await msalInstance.loginRedirect(flowApiTokenRequest);
    } catch (error) {
      if ((error as { errorCode?: string })?.errorCode === 'interaction_in_progress') {
        console.warn('[FIX] Microsoft redirect sign-in is already in progress.');
      } else {
        console.error('Microsoft login failed:', error);
      }
      throw error;
    } finally {
      msalLoginInFlight.current = false;
      setIsLoading(false);
    }
  };

  const isAuthenticated = !!user && !!token && !isTokenExpired(token);

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithMicrosoft,
    logout,
    isLoading,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
